// CompanyFacilitiesView.js - View facilities and their invoices for a specific company
import React, { useState, useEffect, useMemo } from "react";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { ProgressSpinner } from "primereact/progressspinner";
import { Skeleton } from "primereact/skeleton";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import { getCompanyDetails, getFacilityUnits } from "../../../utils/urls";
import Layout from "../component/layout";
import { filterObjectsByAllProperties } from "../../../utils/filterSearch";
import {
  getFacilityInvoices,
  getAllFacilitiesInvoices,
} from "./utils/invoiceFetcher";

const CompanyFacilitiesView = () => {
  const { companyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [company, setCompany] = useState(location.state?.company || null);
  const [facilities, setFacilities] = useState([]);
  const [filteredFacilities, setFilteredFacilities] = useState([]);
  const [facilityUnits, setFacilityUnits] = useState({});
  const [facilityInvoices, setFacilityInvoices] = useState([]);
  const [facilityPayments, setFacilityPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [showInvoicesDialog, setShowInvoicesDialog] = useState(false);

  // Recipient management state
  const [showRecipientsDialog, setShowRecipientsDialog] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [showAddRecipientDialog, setShowAddRecipientDialog] = useState(false);
  const [showEditRecipientDialog, setShowEditRecipientDialog] = useState(false);
  const [showDeleteRecipientDialog, setShowDeleteRecipientDialog] =
    useState(false);
  const [currentRecipient, setCurrentRecipient] = useState({
    phoneNumber: "",
    email: "",
  });
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [loadingCompanyData, setLoadingCompanyData] = useState(false);
  const [isCompanyDataLoaded, setIsCompanyDataLoaded] = useState(false);

  // Month selection for invoice generation
  const [showMonthSelectionDialog, setShowMonthSelectionDialog] =
    useState(false);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [facilityForInvoice, setFacilityForInvoice] = useState(null);
  const [monthSelectionMode, setMonthSelectionMode] = useState("current"); // 'current', 'single', 'multiple'

  // Enhanced stats calculation
  const dashboardStats = useMemo(() => {
    const totalFacilities = facilities.length;
    const totalInvoices = facilityInvoices.length;
    const totalAmount = facilityInvoices.reduce(
      (sum, inv) => sum + (inv.amount || 0),
      0,
    );
    const paidInvoices = facilityInvoices.filter(
      (inv) => inv.status === "paid",
    ).length;
    const pendingInvoices = facilityInvoices.filter(
      (inv) => inv.status === "pending",
    ).length;
    const overdueInvoices = facilityInvoices.filter(
      (inv) => inv.status === "overdue",
    ).length;

    // Calculate total paid amount from valid payments
    const totalPaid = facilityPayments
      .filter((p) => p.status !== "rejected")
      .reduce((sum, p) => sum + (p.amountPaid || 0), 0);

    // Calculate outstanding from pending and overdue invoices only
    const outstandingInvoices = facilityInvoices.filter(
      (inv) => inv.status === "pending" || inv.status === "overdue",
    );
    const totalOutstanding = outstandingInvoices.reduce(
      (sum, inv) => sum + ((inv.amount || 0) - (inv.totalPaid || 0)),
      0,
    );

    return {
      totalFacilities,
      totalInvoices,
      totalAmount,
      totalPaid,
      totalOutstanding,
      paidInvoices,
      pendingInvoices,
      overdueInvoices,
    };
  }, [facilities, facilityInvoices, facilityPayments]);

  // Facility stats
  const facilityStats = useMemo(() => {
    console.log("[FacilityStats] Calculating stats for facilities...");
    console.log("[FacilityStats] Total facilities:", facilities.length);
    console.log("[FacilityStats] Total invoices:", facilityInvoices.length);
    console.log("[FacilityStats] Total payments:", facilityPayments.length);

    return facilities.map((facility) => {
      const facilityInvoicesList = facilityInvoices.filter(
        (inv) => inv.facilityId === facility._id,
      );

      const totalAmount = facilityInvoicesList.reduce(
        (sum, inv) => sum + (inv.amount || 0),
        0,
      );

      // Calculate actual paid amount from payment records
      const facilityInvoiceIds = facilityInvoicesList.map((inv) => inv._id);
      const facilityPaymentsList = facilityPayments.filter((payment) =>
        facilityInvoiceIds.includes(payment.invoiceId),
      );

      const paidAmount = facilityPaymentsList.reduce(
        (sum, payment) => sum + (payment.amountPaid || 0),
        0,
      );

      // Fallback: If no payment records found, use invoice totalPaid field
      const paidAmountFromInvoices = facilityInvoicesList.reduce(
        (sum, inv) => sum + (inv.totalPaid || 0),
        0,
      );

      // Use payment records if available, otherwise fallback to invoice totalPaid
      const finalPaidAmount =
        facilityPaymentsList.length > 0 ? paidAmount : paidAmountFromInvoices;

      const pendingAmount = facilityInvoicesList
        .filter((inv) => inv.status === "pending")
        .reduce((sum, inv) => sum + (inv.amount || 0), 0);

      // Calculate outstanding: unpaid balance from pending and overdue invoices only
      const outstandingInvoices = facilityInvoicesList.filter(
        (inv) => inv.status === "pending" || inv.status === "overdue",
      );
      const outstandingAmount = outstandingInvoices.reduce(
        (sum, inv) => sum + ((inv.amount || 0) - (inv.totalPaid || 0)),
        0,
      );

      const unitsCount = facilityUnits[facility._id]?.length || 0;

      // Log individual facility stats
      console.log(`[FacilityStats] ${facility.name}:`, {
        invoices: facilityInvoicesList.length,
        invoiceIds: facilityInvoiceIds,
        payments: facilityPaymentsList.length,
        totalAmount,
        paidAmount,
        paidAmountFromInvoices,
        finalPaidAmount,
        pendingAmount,
        outstandingAmount,
      });

      return {
        ...facility,
        invoiceCount: facilityInvoicesList.length,
        totalAmount,
        paidAmount: finalPaidAmount,
        pendingAmount,
        outstandingAmount,
        unitsCount,
        status: totalAmount > 0 ? "active" : "inactive",
      };
    });
  }, [facilities, facilityInvoices, facilityUnits, facilityPayments]);

  const getFacilityUnitsData = async (facilities) => {
    const unitsData = {};
    try {
      const unitsPromises = facilities.map(async (facility) => {
        try {
          const response = await makeRequest2(
            `${getFacilityUnits}/${facility._id}`,
            "GET",
          );
          if (response.success) {
            unitsData[facility._id] = response.data;
          } else {
            unitsData[facility._id] = [];
          }
        } catch (error) {
          console.error(
            `Error fetching units for facility ${facility._id}:`,
            error,
          );
          unitsData[facility._id] = [];
        }
      });
      await Promise.all(unitsPromises);
      return unitsData;
    } catch (error) {
      console.error("Error fetching facility units:", error);
      return {};
    }
  };

  const getAllInvoices = async (facilities) => {
    try {
      return await getAllFacilitiesInvoices(facilities, {
        showToast: false,
      });
    } catch (error) {
      return [];
    }
  };

  const getAllPayments = async (invoices) => {
    try {
      const allPayments = [];
      for (const invoice of invoices) {
        try {
          const response = await makeRequest2(
            `/api/core/billing/facility/${invoice.facilityId}/invoices/${invoice._id}/payments`,
            "GET",
          );

          // Handle different response structures
          let paymentsData = [];

          if (Array.isArray(response)) {
            // Response is directly an array of payments
            paymentsData = response;
          } else if (response && response.payments) {
            // Response has payments property
            paymentsData = response.payments;
          } else if (response && response.data) {
            // Response has data property
            if (Array.isArray(response.data)) {
              paymentsData = response.data;
            } else if (response.data.payments) {
              paymentsData = response.data.payments;
            }
          } else if (response && response.success !== false) {
            // Check if response itself might contain payment data
            paymentsData = [];
          }

          // Only add valid payment records (not rejected)
          const validPayments = paymentsData.filter(
            (payment) => payment.status !== "rejected",
          );

          if (validPayments.length > 0) {
            console.log(
              `[Payments] Found ${validPayments.length} valid payment(s) for invoice ${invoice.invoiceNumber}`,
            );
            allPayments.push(...validPayments);
          }
        } catch (err) {
          // Skip failed payment fetches - API endpoint might not exist yet
          console.warn(
            `Failed to fetch payments for invoice ${invoice._id}:`,
            err.message,
          );
        }
      }

      console.log(`[Payments] Total payments loaded: ${allPayments.length}`);
      return allPayments;
    } catch (error) {
      console.error("Error fetching payments:", error);
      return [];
    }
  };

  const getCompanyData = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    console.log(`[CompanyData] Loading data for company: ${companyId}`);

    try {
      // Step 1: Fetch company details
      console.log("[CompanyData] Step 1: Fetching company details...");
      const response = await makeRequest2(
        `${getCompanyDetails}/${companyId}`,
        "GET",
      );

      if (response.success) {
        console.log("[CompanyData] Company details loaded successfully");
        setCompany(response.data.company);

        const facilitiesData = response.data.facilities || [];
        console.log(`[CompanyData] Found ${facilitiesData.length} facilities`);
        setFacilities(facilitiesData);
        setFilteredFacilities(facilitiesData);

        // Step 2: Load units for each facility
        console.log("[CompanyData] Step 2: Loading facility units...");
        const unitsData = await getFacilityUnitsData(facilitiesData);
        setFacilityUnits(unitsData);
        console.log("[CompanyData] Facility units loaded");

        // Step 3: Load invoices for all facilities
        console.log("[CompanyData] Step 3: Loading facility invoices...");
        const invoicesData = await getAllInvoices(facilitiesData);
        setFacilityInvoices(invoicesData);
        console.log(
          `[CompanyData] Loaded ${invoicesData.length} total invoices`,
        );

        // Step 4: Fetch all payments for the invoices
        console.log("[CompanyData] Step 4: Loading payments for invoices...");
        const paymentsData = await getAllPayments(invoicesData);
        setFacilityPayments(paymentsData);
        console.log(
          `[CompanyData] Loaded ${paymentsData.length} total payments`,
        );

        // Calculate totals for summary
        const totalPaid = paymentsData.reduce(
          (sum, payment) => sum + (payment.amountPaid || 0),
          0,
        );
        const totalInvoiced = invoicesData.reduce(
          (sum, inv) => sum + (inv.amount || 0),
          0,
        );
        console.log("[CompanyData] Summary:", {
          facilities: facilitiesData.length,
          invoices: invoicesData.length,
          payments: paymentsData.length,
          totalInvoiced,
          totalPaid,
        });

        toastify("Company data loaded successfully!", "success");
      } else {
        const errorMsg = response.error || "Failed to load company data";
        console.error("[CompanyData] Error:", errorMsg);
        toastify(errorMsg, "error");
      }
    } catch (error) {
      console.error("[CompanyData] Exception occurred:", error);
      toastify(`Error loading company data: ${error.message}`, "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
      console.log("[CompanyData] Loading complete");
    }
  };

  const handleInputSearch = async (value) => {
    try {
      setSearchTerm(value);
      if (value === "") {
        setFilteredFacilities(facilities);
      } else {
        const results = await filterObjectsByAllProperties(facilities, value);
        setFilteredFacilities(results);
      }
    } catch (error) {
      toastify(error.message, "error");
    }
  };

  const handleViewFacilityInvoices = (facility) => {
    const facilityInvoicesList = facilityInvoices.filter(
      (inv) => inv.facilityId === facility._id,
    );
    setSelectedFacility({ ...facility, invoices: facilityInvoicesList });
    setShowInvoicesDialog(true);
  };

  const handleViewInvoice = (invoice) => {
    const facility = facilities.find((f) => f._id === invoice.facilityId);
    navigate(`/core/billing/invoice/${invoice._id}`, {
      state: { invoice, facility },
    });
  };

  const handleOpenMonthSelection = (facility) => {
    console.log("handleOpenMonthSelection called with facility:", facility);
    setFacilityForInvoice(facility);
    setSelectedMonths([]);
    setMonthSelectionMode("current");
    setShowMonthSelectionDialog(true);
    console.log("showMonthSelectionDialog set to true");
  };

  const handleGenerateInvoice = async () => {
    try {
      if (!facilityForInvoice) return;

      const endpoint = `/api/core/billing/facility/${facilityForInvoice._id}/generate-invoice`;

      // Prepare payload with months if selected
      const payload = {};
      if (monthSelectionMode !== "current" && selectedMonths.length > 0) {
        console.log(
          "[Frontend] Selected months (Date objects):",
          selectedMonths,
        );
        // Convert Date objects to YYYY-MM format
        payload.months = selectedMonths.map((date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          return `${year}-${month}`;
        });
        console.log("[Frontend] Converted months to send:", payload.months);
      }

      const message =
        monthSelectionMode === "current"
          ? "Generating invoice for current month..."
          : monthSelectionMode === "single"
            ? "Generating invoice for selected month..."
            : `Generating invoices for ${selectedMonths.length} month(s)...`;

      toastify(message, "info");

      console.log("[Frontend] Sending payload to backend:", payload);
      const response = await makeRequest2(endpoint, "POST", payload);

      if (response.success) {
        toastify(
          response.message || "Invoice(s) generated successfully!",
          "success",
        );
        await getCompanyData(false);
        setShowMonthSelectionDialog(false);
        setSelectedMonths([]);
        setFacilityForInvoice(null);
      } else {
        toastify("Failed to generate invoice: " + response.error, "error");
      }
    } catch (error) {
      toastify("Error generating invoice: " + error.message, "error");
    }
  };

  const getAvailableMonths = () => {
    const months = [];
    const today = new Date();
    // Generate last 12 months
    for (let i = 0; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      months.push(date);
    }
    return months;
  };

  const formatMonthDisplay = (date) => {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
  };

  // Recipient Management Functions
  const fetchRecipients = async (facilityId) => {
    setLoadingRecipients(true);
    try {
      const response = await makeRequest2(
        `/api/core/billing/facility/${facilityId}/recipients`,
        "GET",
      );
      if (response.success) {
        const data = response.data;
        if (Array.isArray(data)) {
          setRecipients(data);
        } else if (data && Array.isArray(data.recipients)) {
          setRecipients(data.recipients);
        } else {
          setRecipients([]);
        }
      } else {
        toastify("Failed to fetch recipients: " + response.error, "error");
      }
    } catch (error) {
      toastify("Error fetching recipients: " + error.message, "error");
    } finally {
      setLoadingRecipients(false);
    }
  };

  const fetchCompanyDataForFacility = async (facilityId) => {
    setLoadingCompanyData(true);
    setIsCompanyDataLoaded(false);
    try {
      const response = await makeRequest2(
        `/api/core/billing/company/facility/${facilityId}`,
        "GET",
      );

      if (response.success && response.data) {
        console.log("Company data received:", response.data); // Debug log
        setCompanyData(response.data);
        setIsCompanyDataLoaded(true);

        // Pre-populate ONLY email (company doesn't have phone field)
        setCurrentRecipient({
          phoneNumber: "", // Leave empty - company doesn't have phone
          email: response.data.email || "", // Use company email
        });

        const companyName =
          response.data.name || response.data.companyName || "Company";
        toastify(`Email pre-filled from ${companyName}`, "success");
      } else {
        toastify("Could not load company data", "warning");
        setCompanyData(null);
        setIsCompanyDataLoaded(false);
      }
    } catch (error) {
      console.error("Error fetching company data:", error);
      setCompanyData(null);
      setIsCompanyDataLoaded(false);
    } finally {
      setLoadingCompanyData(false);
    }
  };

  const handleManageRecipients = (facility) => {
    setSelectedFacility(facility);
    setShowRecipientsDialog(true);
    fetchRecipients(facility._id);
  };

  const handleOpenAddRecipientDialog = async () => {
    setCurrentRecipient({ phoneNumber: "", email: "" });
    setCompanyData(null);
    setIsCompanyDataLoaded(false);
    setShowAddRecipientDialog(true);

    if (selectedFacility) {
      await fetchCompanyDataForFacility(selectedFacility._id);
    }
  };

  const handleAddRecipient = async () => {
    try {
      const response = await makeRequest2(
        `/api/core/billing/facility/${selectedFacility._id}/recipients`,
        "POST",
        currentRecipient,
      );
      if (response.success) {
        toastify("Recipient added successfully!", "success");
        fetchRecipients(selectedFacility._id);
        setShowAddRecipientDialog(false);
        setCurrentRecipient({ phoneNumber: "", email: "" });
        setCompanyData(null);
        setIsCompanyDataLoaded(false);
      } else {
        toastify("Failed to add recipient: " + response.error, "error");
      }
    } catch (error) {
      toastify("Error adding recipient: " + error.message, "error");
    }
  };

  const handleEditRecipient = async () => {
    try {
      const response = await makeRequest2(
        `/api/core/billing/facility/${selectedFacility._id}/recipients/${currentRecipient._id}`,
        "PUT",
        currentRecipient,
      );
      if (response.success) {
        toastify("Recipient updated successfully!", "success");
        fetchRecipients(selectedFacility._id);
        setShowEditRecipientDialog(false);
        setCurrentRecipient({ phoneNumber: "", email: "" });
      } else {
        toastify("Failed to update recipient: " + response.error, "error");
      }
    } catch (error) {
      toastify("Error updating recipient: " + error.message, "error");
    }
  };

  const handleDeleteRecipient = async () => {
    try {
      const response = await makeRequest2(
        `/api/core/billing/facility/${selectedFacility._id}/recipients/${selectedRecipient._id}`,
        "DELETE",
      );
      if (response.success) {
        toastify("Recipient deleted successfully!", "success");
        fetchRecipients(selectedFacility._id);
        setShowDeleteRecipientDialog(false);
        setSelectedRecipient(null);
      } else {
        toastify("Failed to delete recipient: " + response.error, "error");
      }
    } catch (error) {
      toastify("Error deleting recipient: " + error.message, "error");
    }
  };

  const openEditRecipientDialog = (recipient) => {
    setCurrentRecipient(recipient);
    setShowEditRecipientDialog(true);
  };

  const openDeleteRecipientDialog = (recipient) => {
    setSelectedRecipient(recipient);
    setShowDeleteRecipientDialog(true);
  };

  useEffect(() => {
    getCompanyData();
  }, [companyId]);

  useEffect(() => {
    console.log("showMonthSelectionDialog changed:", showMonthSelectionDialog);
  }, [showMonthSelectionDialog]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getStatusSeverity = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return "success";
      case "pending":
        return "warning";
      case "overdue":
        return "danger";
      default:
        return "info";
    }
  };

  // DataTable Templates
  const unitsBodyTemplate = (rowData) => {
    return (
      <Badge
        value={rowData.unitsCount}
        severity="info"
        className="text-capitalize"
      />
    );
  };

  const invoicesBodyTemplate = (rowData) => {
    return (
      <Badge
        value={rowData.invoiceCount}
        severity={rowData.invoiceCount > 0 ? "success" : "secondary"}
        className="text-capitalize"
      />
    );
  };

  const amountBodyTemplate = (rowData) => {
    const collectionRate =
      rowData.totalAmount > 0
        ? (rowData.paidAmount / rowData.totalAmount) * 100
        : 0;

    return (
      <div className="text-end">
        <div className="fw-bold text-primary mb-1">
          {formatCurrency(rowData.totalAmount)}
        </div>
        <div className="d-flex justify-content-end align-items-center gap-1 mb-1">
          <i
            className="ti ti-check-circle text-success"
            style={{ fontSize: "0.75rem" }}
          ></i>
          <span className="small fw-semibold text-success">
            {formatCurrency(rowData.paidAmount)}
          </span>
        </div>
        {rowData.outstandingAmount > 0 && (
          <div className="d-flex justify-content-end align-items-center gap-1">
            <i
              className="ti ti-clock text-warning"
              style={{ fontSize: "0.75rem" }}
            ></i>
            <span className="small text-warning">
              {formatCurrency(rowData.outstandingAmount)}
            </span>
          </div>
        )}
        {rowData.totalAmount > 0 && (
          <div className="progress mt-2" style={{ height: "4px" }}>
            <div
              className={`progress-bar ${
                collectionRate >= 80
                  ? "bg-success"
                  : collectionRate >= 50
                    ? "bg-warning"
                    : "bg-danger"
              }`}
              style={{ width: `${collectionRate}%` }}
              role="progressbar"
              aria-valuenow={collectionRate}
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>
        )}
      </div>
    );
  };

  const statusBodyTemplate = (rowData) => {
    return (
      <Badge
        value={rowData.status}
        severity={rowData.status === "active" ? "success" : "secondary"}
        className="text-capitalize"
      />
    );
  };

  const actionsBodyTemplate = (rowData) => {
    return (
      <div className="d-flex gap-2">
        <Button
          icon="ti ti-receipt"
          className="p-button-sm p-button-text p-button-info"
          onClick={() => handleViewFacilityInvoices(rowData)}
          tooltip="View Invoices"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="ti ti-users"
          className="p-button-sm p-button-text p-button-secondary"
          onClick={() => handleManageRecipients(rowData)}
          tooltip="Manage Recipients"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="ti ti-plus"
          className="p-button-sm p-button-text p-button-success"
          onClick={() => {
            console.log("Generate Invoice button clicked for:", rowData);
            handleOpenMonthSelection(rowData);
          }}
          tooltip="Generate Invoice"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  // Recipient actions template
  const recipientActionsTemplate = (rowData) => {
    return (
      <div className="d-flex gap-2">
        <Button
          icon="ti ti-edit"
          className="p-button-sm p-button-text p-button-primary"
          onClick={() => openEditRecipientDialog(rowData)}
          tooltip="Edit"
        />
        <Button
          icon="ti ti-trash"
          className="p-button-sm p-button-text p-button-danger"
          onClick={() => openDeleteRecipientDialog(rowData)}
          tooltip="Delete"
        />
      </div>
    );
  };

  // Render Overview Dashboard
  const renderOverviewDashboard = () => (
    <div className="row g-4">
      {/* Key Metrics Cards */}
      <div className="col-12">
        <div className="row g-3">
          <div className="col-xl-3 col-md-6">
            <div className="card bg-primary text-white border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h6 className="text-white-50 mb-2">Total Facilities</h6>
                    <h3 className="mb-0 text-white">
                      {loading ? (
                        <Skeleton width="60px" height="32px" />
                      ) : (
                        dashboardStats.totalFacilities
                      )}
                    </h3>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="ti ti-building-warehouse fs-2 text-white-50"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card bg-success text-white border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h6 className="text-white-50 mb-2">Total Invoices</h6>
                    <h3 className="mb-0 text-white">
                      {loading ? (
                        <Skeleton width="80px" height="32px" />
                      ) : (
                        dashboardStats.totalInvoices
                      )}
                    </h3>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="ti ti-receipt fs-2 text-white-50"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card bg-info text-white border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h6 className="text-white-50 mb-2">Total Revenue</h6>
                    <h3 className="mb-0 text-white">
                      {loading ? (
                        <Skeleton width="50px" height="32px" />
                      ) : (
                        formatCurrency(dashboardStats.totalAmount)
                      )}
                    </h3>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="ti ti-currency-dollar fs-2 text-white-50"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card bg-success text-white border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h6 className="text-white-50 mb-2">Total Paid</h6>
                    <h3 className="mb-0 text-white">
                      {loading ? (
                        <Skeleton width="80px" height="32px" />
                      ) : (
                        formatCurrency(dashboardStats.totalPaid || 0).replace(
                          "KES ",
                          "",
                        )
                      )}
                    </h3>
                    <small className="text-white-50">
                      {
                        facilityPayments.filter((p) => p.status !== "rejected")
                          .length
                      }{" "}
                      payment(s)
                    </small>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="ti ti-coin fs-2 text-white-50"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card bg-warning text-white border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h6 className="text-white-50 mb-2">Outstanding</h6>
                    <h3 className="mb-0 text-white">
                      {loading ? (
                        <Skeleton width="80px" height="32px" />
                      ) : (
                        formatCurrency(
                          dashboardStats.totalOutstanding || 0,
                        ).replace("KES ", "")
                      )}
                    </h3>
                    <small className="text-white-50">Pending & Overdue</small>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="ti ti-clock-dollar fs-2 text-white-50"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card bg-danger text-white border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h6 className="text-white-50 mb-2">Overdue Invoices</h6>
                    <h3 className="mb-0 text-white">
                      {loading ? (
                        <Skeleton width="60px" height="32px" />
                      ) : (
                        dashboardStats.overdueInvoices
                      )}
                    </h3>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="ti ti-alert-triangle fs-2 text-white-50"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Statistics Row */}
      <div className="col-12">
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-light d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="ti ti-coins me-2"></i>Payment & Collection Details
            </h6>
            <small className="text-muted">
              Last updated: {new Date().toLocaleTimeString()}
            </small>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <div className="text-center p-3 border rounded bg-light-primary">
                  <div className="text-muted small mb-1">
                    <i className="ti ti-receipt me-1"></i>Total Invoiced
                  </div>
                  <h5 className="mb-0 text-primary fw-bold">
                    {loading ? (
                      <Skeleton width="100px" />
                    ) : (
                      formatCurrency(dashboardStats.totalAmount || 0)
                    )}
                  </h5>
                  <small className="text-muted">
                    {dashboardStats.totalInvoices} invoice(s)
                  </small>
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-center p-3 border rounded bg-light-success">
                  <div className="text-muted small mb-1">
                    <i className="ti ti-check me-1"></i>Total Collected
                  </div>
                  <h5 className="mb-0 text-success fw-bold">
                    {loading ? (
                      <Skeleton width="100px" />
                    ) : (
                      formatCurrency(dashboardStats.totalPaid || 0)
                    )}
                  </h5>
                  <small className="text-muted">
                    {
                      facilityPayments.filter((p) => p.status !== "rejected")
                        .length
                    }{" "}
                    payment(s)
                  </small>
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-center p-3 border rounded bg-light-warning">
                  <div className="text-muted small mb-1">
                    <i className="ti ti-clock-dollar me-1"></i>Outstanding
                  </div>
                  <h5 className="mb-0 text-warning fw-bold">
                    {loading ? (
                      <Skeleton width="100px" />
                    ) : (
                      formatCurrency(dashboardStats.totalOutstanding || 0)
                    )}
                  </h5>
                  <small className="text-muted">Pending & Overdue</small>
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-center p-3 border rounded bg-light-info">
                  <div className="text-muted small mb-1">
                    <i className="ti ti-percentage me-1"></i>Collection Rate
                  </div>
                  <h5 className="mb-0 text-info fw-bold">
                    {loading ? (
                      <Skeleton width="60px" />
                    ) : (
                      `${
                        dashboardStats.totalAmount > 0
                          ? (
                              (dashboardStats.totalPaid /
                                dashboardStats.totalAmount) *
                              100
                            ).toFixed(1)
                          : 0
                      }%`
                    )}
                  </h5>
                  <small className="text-muted">
                    {facilityStats.filter((f) => f.paidAmount > 0).length} of{" "}
                    {facilities.length} facilities
                  </small>
                </div>
              </div>
            </div>
            {!loading &&
              facilityPayments.length === 0 &&
              facilityInvoices.length > 0 && (
                <div className="alert alert-warning mt-3 mb-0">
                  <i className="ti ti-alert-circle me-2"></i>
                  <strong>No payment data loaded.</strong> If payments exist,
                  try refreshing the data or check the browser console for
                  errors.
                </div>
              )}
            {!loading && facilityPayments.length > 0 && (
              <div className="alert alert-info mt-3 mb-0">
                <i className="ti ti-info-circle me-2"></i>
                Showing payment data from{" "}
                <strong>{facilityPayments.length}</strong> payment record(s)
                across all facilities. Paid amounts are calculated from actual
                payment transactions.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Facilities Table */}
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">
                <i className="ti ti-building-warehouse me-2"></i>Facilities for{" "}
                {company?.name}
              </h5>
              <Button
                icon="ti ti-refresh"
                className="p-button-text p-button-sm"
                onClick={() => getCompanyData(false)}
                loading={refreshing}
                tooltip="Refresh Data"
              />
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <ProgressSpinner style={{ width: "50px", height: "50px" }} />
                <p className="mt-3 text-muted">Loading facilities data...</p>
              </div>
            ) : (
              <DataTable
                value={facilityStats}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25, 50]}
                emptyMessage="No facilities found for this company."
                stripedRows
                showGridlines
                responsiveLayout="scroll"
                sortMode="multiple"
              >
                <Column
                  field="name"
                  header="Facility Name"
                  sortable
                  style={{ minWidth: "200px" }}
                />
                <Column
                  field="location"
                  header="Location"
                  sortable
                  style={{ minWidth: "150px" }}
                />
                <Column
                  header="Units"
                  body={unitsBodyTemplate}
                  sortable
                  sortField="unitsCount"
                  style={{ width: "100px", textAlign: "center" }}
                />
                <Column
                  header="Invoices"
                  body={invoicesBodyTemplate}
                  sortable
                  sortField="invoiceCount"
                  style={{ width: "120px", textAlign: "center" }}
                />
                <Column
                  header={
                    <div className="text-center">
                      <div>Financial Summary</div>
                      <small
                        className="text-muted"
                        style={{ fontWeight: "normal", fontSize: "0.7rem" }}
                      >
                        Total | Paid | Outstanding
                      </small>
                    </div>
                  }
                  body={amountBodyTemplate}
                  sortable
                  sortField="totalAmount"
                  style={{ width: "200px" }}
                />
                <Column
                  header="Status"
                  body={statusBodyTemplate}
                  sortable
                  sortField="status"
                  style={{ width: "120px", textAlign: "center" }}
                />
                <Column
                  header="Actions"
                  body={actionsBodyTemplate}
                  style={{ width: "150px", textAlign: "center" }}
                />
              </DataTable>
            )}
          </div>
        </div>
      </div>

      {/* Invoices Dialog */}
      {showInvoicesDialog && selectedFacility && (
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <div className="d-flex align-items-center justify-content-between">
                <h5 className="mb-0 text-white">
                  <i className="ti ti-receipt me-2"></i>Invoices for{" "}
                  {selectedFacility.name}
                </h5>
                <Button
                  icon="ti ti-x"
                  className="p-button-text p-button-sm text-white"
                  onClick={() => setShowInvoicesDialog(false)}
                  tooltip="Close"
                />
              </div>
            </div>
            <div className="card-body">
              {selectedFacility.invoices?.length > 0 ? (
                <DataTable
                  value={selectedFacility.invoices}
                  paginator
                  rows={5}
                  emptyMessage="No invoices found for this facility."
                  stripedRows
                  showGridlines
                  responsiveLayout="scroll"
                >
                  <Column
                    field="invoiceNumber"
                    header="Invoice Number"
                    sortable
                  />
                  <Column
                    field="invoiceDate"
                    header="Invoice Date"
                    sortable
                    body={(rowData) =>
                      new Date(rowData.invoiceDate).toLocaleDateString()
                    }
                  />
                  <Column
                    field="dueDate"
                    header="Due Date"
                    sortable
                    body={(rowData) =>
                      new Date(rowData.dueDate).toLocaleDateString()
                    }
                  />
                  <Column
                    field="amount"
                    header="Amount"
                    sortable
                    body={(rowData) => formatCurrency(rowData.amount)}
                  />
                  <Column
                    field="status"
                    header="Status"
                    sortable
                    body={(rowData) => (
                      <Badge
                        value={rowData.status}
                        severity={getStatusSeverity(rowData.status)}
                        className="text-capitalize"
                      />
                    )}
                  />
                  <Column
                    header="Actions"
                    body={(rowData) => (
                      <Button
                        icon="ti ti-eye"
                        className="p-button-sm p-button-text p-button-info"
                        onClick={() => handleViewInvoice(rowData)}
                        tooltip="View Invoice"
                      />
                    )}
                  />
                </DataTable>
              ) : (
                <div className="text-center py-4">
                  <i className="ti ti-inbox fs-1 text-muted"></i>
                  <p className="text-muted mt-2">
                    No invoices found for this facility.
                  </p>
                  <Button
                    label="Generate Invoice"
                    icon="ti ti-plus"
                    className="p-button-sm"
                    onClick={() => handleGenerateInvoice(selectedFacility._id)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Layout>
      {/* Header */}
      <div className="page-header bg-light border-bottom">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-8">
              <ul className="breadcrumb mb-2">
                <li className="breadcrumb-item">
                  <Link to="/core/dashboard" className="text-muted">
                    <i className="ti ti-home me-1"></i>Dashboard
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/core/billing" className="text-muted">
                    Billing Management
                  </Link>
                </li>
                <li className="breadcrumb-item active text-dark">
                  {company?.name || "Company Details"}
                </li>
              </ul>
              <div className="page-header-title">
                <h2 className="mb-1 text-dark fw-semibold">
                  <i className="ti ti-building me-2 text-primary"></i>
                  {company?.name || "Loading..."}
                </h2>
                <p className="mb-0 text-muted">
                  View and manage facilities and invoices for this company
                </p>
              </div>
            </div>
            <div className="col-md-4 text-end">
              <div className="d-flex gap-2 justify-content-end">
                <Button
                  label="Back to Companies"
                  icon="ti ti-arrow-left"
                  className="p-button-outlined"
                  onClick={() => navigate("/core/billing")}
                />
                <div className="d-flex gap-2 justify-content-end">
                  <Button
                    icon="ti ti-refresh"
                    label="Refresh All Data"
                    className="p-button-outlined p-button-secondary"
                    onClick={() => getCompanyData(false)}
                    loading={refreshing}
                    tooltip="Refresh facilities, invoices, and payments"
                  />
                  <Button
                    icon="ti ti-database"
                    label="Payment Stats"
                    className="p-button-outlined p-button-info"
                    onClick={() => {
                      console.log("=== PAYMENT STATISTICS ===");
                      console.log(
                        "Total Payments Loaded:",
                        facilityPayments.length,
                      );
                      console.log("Total Invoices:", facilityInvoices.length);
                      console.log("Facilities:", facilities.length);
                      console.log("Payment Details:", facilityPayments);
                      toastify(
                        `Payments: ${facilityPayments.length} | Invoices: ${facilityInvoices.length}`,
                        "info",
                      );
                    }}
                    tooltip="View payment statistics in console"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          {/* Search Bar */}
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="row align-items-center g-3">
                <div className="col-md-12">
                  <div className="position-relative">
                    <i className="ti ti-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                    <InputText
                      value={searchTerm}
                      onChange={(e) => handleInputSearch(e.target.value)}
                      placeholder="Search facilities..."
                      className="form-control ps-5"
                      style={{ paddingLeft: "2.5rem" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="mt-3">{renderOverviewDashboard()}</div>
        </div>
      </div>

      {/* Recipients Management Dialog */}
      <Dialog
        visible={showRecipientsDialog}
        onHide={() => setShowRecipientsDialog(false)}
        header={`Manage Recipients - ${selectedFacility?.name}`}
        modal
        style={{ width: "70vw" }}
        maximizable
      >
        <div className="mb-3">
          <Button
            label="Add Recipient"
            icon="ti ti-plus"
            className="p-button-sm"
            onClick={handleOpenAddRecipientDialog}
          />
        </div>
        {loadingRecipients ? (
          <div className="text-center py-4">
            <ProgressSpinner style={{ width: "40px", height: "40px" }} />
            <p className="mt-2 text-muted">Loading recipients...</p>
          </div>
        ) : (
          <DataTable
            value={recipients}
            paginator
            rows={10}
            emptyMessage="No recipients found. Click 'Add Recipient' to add one."
            stripedRows
            showGridlines
          >
            <Column field="email" header="Email" sortable />
            <Column field="phoneNumber" header="Phone Number" sortable />
            <Column
              header="Actions"
              body={recipientActionsTemplate}
              style={{ width: "150px", textAlign: "center" }}
            />
          </DataTable>
        )}
      </Dialog>

      {/* Add Recipient Dialog */}
      <Dialog
        visible={showAddRecipientDialog}
        onHide={() => {
          setShowAddRecipientDialog(false);
          setCompanyData(null);
          setIsCompanyDataLoaded(false);
        }}
        header="Add Recipient"
        modal
        style={{ width: "40vw" }}
      >
        <div className="p-fluid">
          {loadingCompanyData && (
            <div className="text-center mb-3">
              <ProgressSpinner style={{ width: "30px", height: "30px" }} />
              <p className="text-muted mt-2">Loading company data...</p>
            </div>
          )}

          {isCompanyDataLoaded && companyData && (
            <Message
              severity="info"
              text={`Email pre-filled from ${
                companyData.name || companyData.companyName || "company"
              }. Please add phone number.`}
              className="mb-3"
            />
          )}

          <div className="field mb-3">
            <label htmlFor="phoneNumber">
              Phone Number <span className="text-danger">*</span>
            </label>
            <InputText
              id="phoneNumber"
              value={currentRecipient.phoneNumber}
              onChange={(e) =>
                setCurrentRecipient({
                  ...currentRecipient,
                  phoneNumber: e.target.value,
                })
              }
              placeholder="Enter phone number (required)"
              disabled={loadingCompanyData}
            />
            <small className="text-muted">
              Company data doesn't include phone - please enter manually
            </small>
          </div>

          <div className="field mb-3">
            <label htmlFor="email">
              Email <span className="text-danger">*</span>
            </label>
            <InputText
              id="email"
              type="email"
              value={currentRecipient.email}
              onChange={(e) =>
                setCurrentRecipient({
                  ...currentRecipient,
                  email: e.target.value,
                })
              }
              placeholder="Enter email address"
              disabled={loadingCompanyData}
            />
            {companyData && (
              <small className="text-success">
                ✓ Pre-filled from company - you can edit before saving
              </small>
            )}
          </div>

          <div className="d-flex gap-2 justify-content-end mt-4">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => {
                setShowAddRecipientDialog(false);
                setCompanyData(null);
                setIsCompanyDataLoaded(false);
              }}
            />
            <Button
              label="Add Recipient"
              icon="ti ti-check"
              onClick={handleAddRecipient}
              disabled={
                !currentRecipient.email || !currentRecipient.phoneNumber
              }
            />
          </div>
        </div>
      </Dialog>

      {/* Edit Recipient Dialog */}
      <Dialog
        visible={showEditRecipientDialog}
        onHide={() => setShowEditRecipientDialog(false)}
        header="Edit Recipient"
        modal
        style={{ width: "40vw" }}
      >
        <div className="p-fluid">
          <div className="field mb-3">
            <label htmlFor="editPhoneNumber">Phone Number</label>
            <InputText
              id="editPhoneNumber"
              value={currentRecipient.phoneNumber}
              onChange={(e) =>
                setCurrentRecipient({
                  ...currentRecipient,
                  phoneNumber: e.target.value,
                })
              }
            />
          </div>
          <div className="field mb-3">
            <label htmlFor="editEmail">Email</label>
            <InputText
              id="editEmail"
              type="email"
              value={currentRecipient.email}
              onChange={(e) =>
                setCurrentRecipient({
                  ...currentRecipient,
                  email: e.target.value,
                })
              }
            />
          </div>
          <div className="d-flex gap-2 justify-content-end mt-4">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setShowEditRecipientDialog(false)}
            />
            <Button
              label="Update"
              icon="ti ti-check"
              onClick={handleEditRecipient}
            />
          </div>
        </div>
      </Dialog>

      {/* Delete Recipient Confirm Dialog */}
      <Dialog
        visible={showDeleteRecipientDialog}
        onHide={() => setShowDeleteRecipientDialog(false)}
        header="Confirm Delete"
        modal
        style={{ width: "30vw" }}
      >
        <p>Are you sure you want to delete this recipient?</p>
        {selectedRecipient && (
          <div className="mt-3">
            <strong>Email:</strong> {selectedRecipient.email}
            <br />
            <strong>Phone:</strong> {selectedRecipient.phoneNumber}
          </div>
        )}
        <div className="d-flex gap-2 justify-content-end mt-4">
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => setShowDeleteRecipientDialog(false)}
          />
          <Button
            label="Delete"
            icon="ti ti-trash"
            className="p-button-danger"
            onClick={handleDeleteRecipient}
          />
        </div>
      </Dialog>

      {/* Month Selection Dialog for Invoice Generation */}
      <Dialog
        visible={showMonthSelectionDialog}
        onHide={() => {
          setShowMonthSelectionDialog(false);
          setSelectedMonths([]);
          setFacilityForInvoice(null);
        }}
        header={`Generate Invoice - ${facilityForInvoice?.name}`}
        modal
        style={{ width: "40vw" }}
      >
        <div className="p-fluid">
          <div className="mb-4">
            <Message
              severity="info"
              text="Choose to generate invoice(s) for the current month, a specific past month, or multiple months."
              className="mb-3"
            />
          </div>

          {/* Selection Mode Radio Buttons */}
          <div className="field mb-4">
            <label className="mb-2 fw-bold">Generation Mode</label>
            <div className="flex flex-column gap-3">
              <div className="flex align-items-center">
                <input
                  type="radio"
                  id="current"
                  name="monthMode"
                  value="current"
                  checked={monthSelectionMode === "current"}
                  onChange={(e) => {
                    setMonthSelectionMode(e.target.value);
                    setSelectedMonths([]);
                  }}
                  className="me-2"
                />
                <label htmlFor="current" className="mb-0">
                  Current Month ({formatMonthDisplay(new Date())})
                </label>
              </div>
              <div className="flex align-items-center">
                <input
                  type="radio"
                  id="single"
                  name="monthMode"
                  value="single"
                  checked={monthSelectionMode === "single"}
                  onChange={(e) => {
                    setMonthSelectionMode(e.target.value);
                    setSelectedMonths([]);
                  }}
                  className="me-2"
                />
                <label htmlFor="single" className="mb-0">
                  Single Past Month
                </label>
              </div>
              <div className="flex align-items-center">
                <input
                  type="radio"
                  id="multiple"
                  name="monthMode"
                  value="multiple"
                  checked={monthSelectionMode === "multiple"}
                  onChange={(e) => {
                    setMonthSelectionMode(e.target.value);
                    setSelectedMonths([]);
                  }}
                  className="me-2"
                />
                <label htmlFor="multiple" className="mb-0">
                  Multiple Months
                </label>
              </div>
            </div>
          </div>

          {/* Single Month Calendar */}
          {monthSelectionMode === "single" && (
            <div className="field mb-3">
              <label htmlFor="singleMonth">Select Month</label>
              <Calendar
                id="singleMonth"
                value={selectedMonths[0] || null}
                onChange={(e) => setSelectedMonths(e.value ? [e.value] : [])}
                view="month"
                dateFormat="MM yy"
                placeholder="Select a month"
                maxDate={new Date()}
                showIcon
                className="w-100"
              />
              <small className="text-muted">
                {selectedMonths.length > 0
                  ? `Selected: ${formatMonthDisplay(selectedMonths[0])}`
                  : "Please select a month"}
              </small>
            </div>
          )}

          {/* Multiple Months Selection */}
          {monthSelectionMode === "multiple" && (
            <div className="field mb-3">
              <label htmlFor="monthSelection">Select Months</label>
              <Calendar
                id="monthSelection"
                value={selectedMonths}
                onChange={(e) => setSelectedMonths(e.value || [])}
                view="month"
                dateFormat="MM yy"
                placeholder="Select multiple months"
                maxDate={new Date()}
                showIcon
                selectionMode="multiple"
                inline
                className="w-100"
              />
              <small className="text-muted">
                {selectedMonths.length === 0
                  ? "Please select one or more months"
                  : `${selectedMonths.length} month(s) selected: ${selectedMonths.map((m) => formatMonthDisplay(m)).join(", ")}`}
              </small>
            </div>
          )}

          {/* Summary Box */}
          <div className="field mb-3">
            <div className="bg-light p-3 rounded">
              <strong className="d-block mb-2">
                <i className="ti ti-info-circle me-2"></i>Summary:
              </strong>
              {monthSelectionMode === "current" ? (
                <div className="text-muted">
                  Will generate invoice for current month:{" "}
                  <strong>{formatMonthDisplay(new Date())}</strong>
                </div>
              ) : selectedMonths.length === 0 ? (
                <div className="text-warning">Please select month(s) above</div>
              ) : (
                <div>
                  <div className="text-success mb-2">
                    Will generate {selectedMonths.length} invoice(s) for:
                  </div>
                  <ul className="mb-0">
                    {selectedMonths
                      .sort((a, b) => b.getTime() - a.getTime())
                      .map((month, index) => (
                        <li key={index}>{formatMonthDisplay(month)}</li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="d-flex gap-2 justify-content-end mt-4">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => {
                setShowMonthSelectionDialog(false);
                setSelectedMonths([]);
                setFacilityForInvoice(null);
              }}
            />
            <Button
              label={
                monthSelectionMode === "current"
                  ? "Generate for Current Month"
                  : monthSelectionMode === "single"
                    ? "Generate for Selected Month"
                    : `Generate for ${selectedMonths.length} Month(s)`
              }
              icon="ti ti-receipt"
              onClick={handleGenerateInvoice}
              disabled={
                (monthSelectionMode === "single" ||
                  monthSelectionMode === "multiple") &&
                selectedMonths.length === 0
              }
            />
          </div>
        </div>
      </Dialog>
    </Layout>
  );
};

export default CompanyFacilitiesView;
